import { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardList, ExternalLink, Mail, Info } from "lucide-react";

/**
 * Registration page — hosts the Google Form embed.
 * Once the user provides the embed URL, paste it into GOOGLE_FORM_URL.
 * Use the /viewform?embedded=true variant for proper iframe rendering.
 */
const GOOGLE_FORM_URL =
  "https://pci.jotform.com/form/261122565250044";

const STEPS = [
  {
    n: "01",
    title: "Raduna il team",
    desc: "Minimo 3, massimo 5 giocatori. Serve un capitano che gestirà le comunicazioni.",
  },
  {
    n: "02",
    title: "Compila il form",
    desc: "Dati dei giocatori, nome del team, categoria. Bastano 3 minuti.",
  },
  {
    n: "03",
    title: "Conferma e paga",
    desc: "Riceverai una email con i dettagli per confermare l'iscrizione e la quota.",
  },
  {
    n: "04",
    title: "Allenati. Sempre.",
    desc: "Il 2 luglio 2026 si scende in campo. Arriva preparato: non si scherza.",
  },
];

export default function Registration() {
  const [iframeError, setIframeError] = useState(false);
  const hasForm = Boolean(GOOGLE_FORM_URL);

  return (
    <div data-testid="page-registration" className="text-white">
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-xs uppercase tracking-[0.3em] text-[#FF4500] font-bold"
        >
          / Iscrizioni aperte
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 font-bebas uppercase leading-[0.85] text-6xl sm:text-7xl md:text-[9vw]"
        >
          Iscrivi il <br />
          tuo <span className="text-[#FF4500]">team</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 max-w-2xl text-[#A3A3A3] leading-relaxed"
        >
          Compila il modulo qui sotto per entrare ufficialmente nel tabellone del
          Break the Rules 3x3. Posti limitati, si procede per ordine di arrivo.
        </motion.p>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group bg-[#0A0A0A] border border-[#2A2A2A] p-6 hover:border-[#FF4500] transition-colors"
              data-testid={`registration-step-${s.n}`}
            >
              <span className="font-bebas text-6xl leading-none text-[#FF4500]">
                {s.n}
              </span>
              <h3 className="mt-4 font-bebas text-2xl uppercase leading-none">
                {s.title}
              </h3>
              <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section
        data-testid="registration-form-section"
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 border border-[#FF4500] flex items-center justify-center shrink-0">
              <ClipboardList size={20} className="text-[#FF4500]" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3] font-bold">
                Modulo di iscrizione
              </p>
              <h2 className="mt-1 font-bebas text-3xl md:text-4xl uppercase leading-none">
                Compila tutti i campi
              </h2>
            </div>
          </div>

          {hasForm && (
            <a
              href={GOOGLE_FORM_URL.replace("?embedded=true", "").replace(
                "&embedded=true",
                ""
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="registration-open-external"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white hover:text-[#FF4500] transition-colors"
            >
              Apri in una nuova scheda
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        <div className="relative border-2 border-[#FF4500] bg-[#0A0A0A] p-1">
          {hasForm && !iframeError ? (
            <iframe
              title="Modulo iscrizione Break the Rules 3x3"
              src={GOOGLE_FORM_URL}
              className="w-full bg-white"
              style={{ minHeight: "1100px" }}
              onError={() => setIframeError(true)}
              data-testid="registration-iframe"
            />
          ) : (
            <div
              data-testid="registration-placeholder"
              className="w-full min-h-[600px] flex flex-col items-center justify-center text-center px-6 py-16 bg-[#050505] border border-dashed border-[#2A2A2A]"
            >
              <span className="w-16 h-16 border-2 border-[#FF4500] flex items-center justify-center">
                <Info size={26} className="text-[#FF4500]" />
              </span>

              <p className="mt-8 font-bebas text-5xl md:text-6xl uppercase leading-none">
                Form in arrivo
              </p>

              <p className="mt-6 max-w-xl text-[#A3A3A3] leading-relaxed">
                Il modulo Google per le iscrizioni sarà integrato qui non appena
                disponibile. Nel frattempo puoi scriverci per prenotare il tuo
                posto.
              </p>

              <a
                href="mailto:infobtr3x3@gmail.com?subject=Iscrizione%20Break%20the%20Rules%203x3"
                data-testid="registration-fallback-email"
                className="mt-10 inline-flex items-center gap-3 bg-[#FF4500] text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-colors duration-300"
              >
                <Mail size={18} />
                Scrivici per iscriverti
              </a>

              <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                infobtr3x3@gmail.com · 329 5653110
              </p>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-[#A3A3A3] leading-relaxed max-w-2xl">
          Inviando il form accetti il regolamento ufficiale del torneo e la
          gestione dei dati personali ai soli fini organizzativi.
        </p>
      </section>
    </div>
  );
}
